import { Fragment, type PropsWithChildren } from "react"
import { Equals, Hash } from "@phosphor-icons/react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import HoverCardRow from "~/components/reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "~/components/reusable/hovercard/HoverCardRowWrapper"
import HoverCardExpandableRow from "~/components/reusable/hovercard/HoverCardExpandableRow"
import { Accordion } from "~/components/reusable/Accordion"
import type { ContributingMatch } from "~/hooks/useCollectionComparisonTableData"
import { useUser } from "~/providers/userProvider"
import { formatNumber } from "~/utils/formatting"
import { formatVariantId } from "~/utils/helpers"

type Props = PropsWithChildren & {
  matches: ContributingMatch[]
  getMatchValue: (matchId: string) => number
  result: number
  valueLabel?: string
}

export default function WeightedAverageFormulaSection({
  children,
  matches,
  getMatchValue,
  result,
  valueLabel = "Value",
}: Props) {
  const { permissions } = useUser()

  if (!permissions.isStaff) return children

  const rows = matches.map((m) => ({
    id: `#${formatVariantId(m.match)}`,
    value: getMatchValue(m.match),
    weight: m.similarity,
  }))

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        className="w-90 p-0 overflow-hidden"
        side="right"
        sideOffset={8}
        avoidCollisions
      >
        <div className="flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-base bg-base">
            <span className="label-base text-fg-base">Weighted Average</span>
            <div className="text-2xl font-semibold mt-1 text-fg-base">
              {formatNumber(result, 0)}{" "}
              <span className="text-base font-normal text-fg-subtle">units</span>
            </div>
            <p className="paragraph-sm text-fg-subtle mt-1">
              Match scores are stored with full precision. The percentages shown are rounded, so the
              result may differ from a manual calculation using those values.
            </p>
          </div>

          <HoverCardRowWrapper>
            {/* Shared grid: header + data rows, 4 columns: icon | variant | weight | value */}
            <div
              className="px-4 pt-2 pb-1 paragraph-sm"
              style={{
                display: "grid",
                gridTemplateColumns: "16px 1fr auto 100px",
                gap: "2px 12px",
              }}
            >
              {/* Column headers */}
              <div />
              <span className="label-xs text-fg-subtle pb-1">Variant</span>
              <span className="label-xs text-fg-subtle text-right pb-1">Weight</span>
              <span className="label-xs text-fg-subtle text-right pb-1 truncate">{valueLabel}</span>

              {/* Data rows */}
              {rows.map((r, i) => (
                <Fragment key={i}>
                  <Hash size={14} className="text-fg-subtle self-center" />
                  <span className="text-fg-subtle font-mono truncate">{r.id}</span>
                  <span className="tabular-nums font-mono text-fg-subtle text-right">
                    {r.weight.toFixed(4)}
                  </span>
                  <span className="tabular-nums font-medium text-fg-base text-right">
                    {formatNumber(r.value, 0)}
                  </span>
                </Fragment>
              ))}
            </div>

            <div>
              <hr className="border-b border-strong" />
            </div>

            {/* Formula accordion */}
            <Accordion type="multiple">
              <HoverCardExpandableRow
                HoverCardRow={
                  <HoverCardRow
                    icon={<Equals />}
                    label="Weighted average"
                    value={formatNumber(result, 0)}
                  />
                }
              >
                <div className="px-4 pb-3 pt-1 font-mono paragraph-sm flex items-center gap-2 justify-center">
                  <div className="flex flex-col items-center gap-0.5">
                    {/* Numerator */}
                    <div className="flex items-baseline gap-1 flex-wrap justify-center">
                      {rows.map((r, i) => (
                        <Fragment key={i}>
                          {i > 0 && <span className="text-fg-subtle">+</span>}
                          <span className="text-fg-subtle">(</span>
                          <span className="text-fg-base tabular-nums">
                            {formatNumber(r.value, 0)}
                          </span>
                          <span className="text-fg-subtle px-0.5">×</span>
                          <span className="text-fg-base tabular-nums">{r.weight.toFixed(4)}</span>
                          <span className="text-fg-subtle">)</span>
                        </Fragment>
                      ))}
                    </div>
                    {/* Fraction line */}
                    <div className="w-full border-t-2 border-fg-muted" />
                    {/* Denominator */}
                    <div className="flex items-baseline gap-1 justify-center">
                      {rows.map((r, i) => (
                        <Fragment key={i}>
                          {i > 0 && <span className="text-fg-subtle">+</span>}
                          <span className="text-fg-base tabular-nums">{r.weight.toFixed(4)}</span>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                  <span className="text-fg-subtle">=</span>
                  <span className="tabular-nums font-semibold text-fg-base">
                    {formatNumber(result, 0)}
                  </span>
                </div>
              </HoverCardExpandableRow>
            </Accordion>
          </HoverCardRowWrapper>
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
